import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, FileVideo, Image, Music, X, CheckCircle } from "lucide-react";

const MAX_SIZE_MB = 200;
const ALLOWED_VIDEO = [".mp4", ".mov", ".avi", ".webm", ".mpg", ".mpeg", ".mkv", ".m4v"];
const ALLOWED_IMAGE = [".jpg", ".jpeg", ".png", ".webp", ".gif"];
const ALLOWED_AUDIO = [".mp3", ".m4a", ".wav"];
const ALLOWED = [...ALLOWED_VIDEO, ...ALLOWED_IMAGE, ...ALLOWED_AUDIO];

function FileIcon({ name }) {
  const ext = ("." + (name || "").split(".").pop()).toLowerCase();
  if (ALLOWED_IMAGE.includes(ext)) return <Image className="w-6 h-6 text-brand-primary" />;
  if (ALLOWED_AUDIO.includes(ext)) return <Music className="w-6 h-6 text-brand-primary" />;
  return <FileVideo className="w-6 h-6 text-brand-primary" />;
}

export default function UploadZone({
  onFileSelected,
  disabled,
  allowedExtensions = ALLOWED,
  helperText = null,
  restrictionLabel = null,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const accepted = (
    Array.isArray(allowedExtensions) && allowedExtensions.length
      ? allowedExtensions
      : ALLOWED
  ).map((ext) => String(ext || "").toLowerCase());

  const validate = useCallback((f) => {
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!accepted.includes(ext)) {
      return `Unsupported format. Allowed: ${accepted.join(", ")}`;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File too large. Max ${MAX_SIZE_MB}MB.`;
    }
    return null;
  }, [accepted]);

  const handleFile = useCallback(
    (f) => {
      const err = validate(f);
      if (err) {
        setError(err);
        return;
      }
      setError(null);
      setFile(f);
      onFileSelected(f);
    },
    [validate, onFileSelected]
  );

  const onDragOver = (e) => { e.preventDefault(); if (!disabled) setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };
  const onInputChange = (e) => {
    if (e.target.files?.[0]) handleFile(e.target.files[0]);
  };
  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null);
    setError(null);
    onFileSelected(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <motion.div
        onClick={() => !disabled && !file && inputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        animate={isDragging ? { scale: 1.01 } : { scale: 1 }}
        className={`
          relative rounded-md border-2 border-dashed transition-all duration-200 cursor-pointer
          ${file ? "border-brand-primary/60 bg-brand-primary/5" : "border-border bg-card"}
          ${isDragging ? "border-brand-primary bg-brand-primary/10 glow-animate" : ""}
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-brand-primary/50 hover:bg-card/80"}
          min-h-[180px] flex items-center justify-center p-8
        `}
        data-testid="upload-zone"
        aria-label="Video upload drop zone"
      >
        {restrictionLabel && (
          <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border border-amber-400/40 bg-amber-500/10 text-amber-400">
            {restrictionLabel}
          </span>
        )}

        {/* Scanlines overlay */}
        <div
          className="absolute inset-0 rounded-md pointer-events-none opacity-5"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.1) 3px, rgba(255,255,255,0.1) 4px)" }}
        />

        {file ? (
          <div className="flex items-center gap-4 w-full" data-testid="file-selected">
            <div className="w-12 h-12 rounded-sm bg-brand-primary/10 border border-brand-primary/30 flex items-center justify-center flex-shrink-0">
              <FileIcon name={file.name} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate" data-testid="file-name">{file.name}</div>
              <div className="text-xs font-mono text-muted-foreground mt-0.5">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-verdict-real" />
              {!disabled && (
                <button
                  onClick={clearFile}
                  className="w-7 h-7 rounded-sm flex items-center justify-center hover:bg-secondary transition-colors"
                  data-testid="clear-file-btn"
                  aria-label="Remove file"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className={`w-14 h-14 rounded-sm border flex items-center justify-center transition-colors ${
              isDragging ? "border-brand-primary bg-brand-primary/10" : "border-border bg-secondary/50"
            }`}>
              <Upload className={`w-6 h-6 transition-colors ${isDragging ? "text-brand-primary" : "text-muted-foreground"}`} />
            </div>
            <div>
              <div className="font-medium text-sm">
                {isDragging ? "Drop to analyze" : "Drag & drop your media file"}
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">
                {helperText || `Video, Image, Audio - max ${MAX_SIZE_MB}MB`}
              </div>
            </div>
            <span className="text-xs text-muted-foreground border border-border rounded-sm px-3 py-1.5 hover:border-brand-primary/40 transition-colors">
              or click to browse
            </span>
          </div>
        )}
      </motion.div>

      {error && (
        <p className="text-xs text-red-500 font-mono" data-testid="upload-error">{error}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accepted.join(",")}
        onChange={onInputChange}
        className="hidden"
        data-testid="file-input"
      />
    </div>
  );
}
